/**
 * Whole-book navigator: live miniatures of every spread (cover →
 * interiors → back) plus an add-spread ghost card. Ported from the design
 * prototype's spread-editor/panels.jsx.
 */
import type { SpreadElement } from '@historyjki/shared';
import { getCanvasAspect } from '../../utils/spreadGeometry';
import type { ComposeStore, SpreadView } from '../../hooks/useComposeStage';
import { SpreadImage } from './SpreadImage';
import { IconPlus } from './icons';

function MiniSpread({ spread, aspect }: { spread: SpreadView; aspect: number }) {
  const isSingle = spread.kind === 'cover' || spread.kind === 'back';
  const els = [...spread.elements].sort((a, b) => a.z - b.z);
  return (
    <div
      className={`mini ${isSingle ? 'mini--single' : ''}`}
      style={{ ['--spread-ar' as string]: aspect }}
    >
      {!isSingle && <div className="mini__gutter" />}
      {els.map((el: SpreadElement) => (
        <div
          key={el.id}
          className={`mini__el mini__el--${el.type}`}
          style={{
            left: `${el.x}%`,
            top: `${el.y}%`,
            width: `${el.w}%`,
            height: `${el.h}%`,
            zIndex: el.z,
          }}
        >
          {el.type === 'image' ? (
            // Crop is applied (it changes the composition) but edge masks
            // are skipped — cqw units don't resolve inside the mini.
            <SpreadImage el={el} boxAspect={(el.w / el.h) * aspect} plainEdges />
          ) : (
            <span className="mini__lines" />
          )}
        </div>
      ))}
    </div>
  );
}

export function SpreadNavigator({ store }: { store: ComposeStore }) {
  const { project, spreads, currentId, selectSpread, labelFor, addSpread } = store;
  const format = project?.format_preset ?? null;
  return (
    <div className="nav-strip" data-testid="spread-navigator">
      {spreads.map((s) => {
        const { label, pages } = labelFor(s);
        return (
          <button
            key={s.id}
            type="button"
            className={`nav-card ${s.id === currentId ? 'nav-card--current' : ''}`}
            onClick={() => selectSpread(s.id)}
            aria-label={`Go to ${label}`}
            aria-pressed={s.id === currentId}
          >
            <MiniSpread spread={s} aspect={getCanvasAspect(format, s.kind)} />
            <span className="nav-card__label">
              {s.kind === 'spread' ? label.replace('Spread ', '') : label}
              {pages && <span className="nav-card__pages">{pages}</span>}
            </span>
          </button>
        );
      })}
      {spreads.length > 0 && (
        <button
          type="button"
          className="nav-card nav-card--add"
          onClick={() => void addSpread()}
          aria-label="Add spread"
          title="Add spread"
        >
          <div className="mini" style={{ ['--spread-ar' as string]: getCanvasAspect(format, 'spread') }}>
            <IconPlus />
          </div>
          <span className="nav-card__label">Add</span>
        </button>
      )}
    </div>
  );
}
